import React, { useState, useEffect, useRef, useCallback } from "react";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import { extractCleanSequences } from "../../../../../utilities/textUtils";
import { pdfjs } from "react-pdf";
import { useHookstate } from "@hookstate/core";
import globalAuthStateStore from "../../../../../stores/globalAuthStateStore";
import { VirtualLibrarianService } from "../../../../../services/VirtualLibrarianService";
import videoIcon from "../../../../../content/icons/Video.svg";
import ContentModal from "../../certificationQuestion/ContentModal";
import { getSeconds } from "../utilities/timeUtils";
// import { useNavigate } from "react-router-dom";
import { useNavigate } from "../../../../../components/Navigate";
import { ItemServiceFactory } from "../../../../../services/ItemServiceFactory";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import PdfViewer from "./PdfViewer";
import "./citationResults.scss";
import CitationTooltip from "./citationToolTip";

// Hide scrollbar while allowing scrolling
const hideScrollbarStyle = document.createElement("style");
hideScrollbarStyle.textContent = `
  .hide-scrollbar::-webkit-scrollbar {
    display: none;  /* Chrome, Safari, Opera */
  }
`;
document.head.appendChild(hideScrollbarStyle);

// Set the worker source to be compatible with the installed react-pdf version
const workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

const VirtualLibrarianCitationResults = ({ citations }) => {
  const navigate = useNavigate();

  const [combinedData, setCombinedData] = useState([]);
  const [selectedCitation, setSelectedCitation] = useState(null);
  const [selectedCitationContent, setSelectedCitationContent] = useState("");
  const [searchTerms, setSearchTerms] = useState([]);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [imageLoadFailed, setImageLoadFailed] = useState({});
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [isPdf, setIsPdf] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [leftPanelWidth, setLeftPanelWidth] = useState(300);
  const [isResizing, setIsResizing] = useState(false);
  const textLayerRef = useRef(null);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchTextForPdf, setDebouncedSearchTextForPdf] =
    useState("");
  const [viewerWidth, setViewerWidth] = useState(null);
  const targetPageRef = useRef(1);
  const initialLoadDoneRef = useRef(false);
  const pdfContainerRef = useRef(null);
  const containerRef = useRef(null);

  const authStore = useHookstate(globalAuthStateStore);
  const vlService = React.useMemo(
    () => new VirtualLibrarianService(authStore),
    [authStore]
  );
  const itemService = React.useMemo(
    () => ItemServiceFactory.GetItemService("content", authStore),
    [authStore]
  );

  useEffect(() => {
    async function fetchData() {
      try {
        // Check if citations exists and is an array
        if (!Array.isArray(citations) || citations.length === 0) {
          console.warn("No citations provided or citations is not an array");
          return;
        }

        // Filter out any undefined or null citations and get unique content IDs
        const validCitations = citations.filter(
          (citation) => citation?.content_id
        );
        const uniqueContentIds = [
          ...new Set(validCitations.map((citation) => citation.content_id)),
        ];

        // Only proceed if we have valid content IDs
        if (uniqueContentIds.length === 0) {
          console.warn("No valid content IDs found in citations");
          return;
        }

        const results = await Promise.all(
          uniqueContentIds.map((contentId) =>
            itemService
              .getOperation("SearchContent", contentId, undefined, false)
              .catch((error) => {
                console.error(
                  `Error fetching content for ID ${contentId}:`,
                  error
                );
                return null;
              })
          )
        );

        // Filter out any failed requests
        const validResults = results.filter(Boolean);

        // Process results only if we have any
        if (validResults.length > 0) {
          const thumbUrls = {};
          validResults.forEach((r) => {
            // Add null checks for the response structure
            if (
              r?.[0]?.Items?.[0]?.ContentId &&
              r[0].Items[0].ThumbnailFileUrl
            ) {
              thumbUrls[r[0].Items[0].ContentId] =
                r[0].Items[0].ThumbnailFileUrl;
            }
          });
          setThumbnailUrls(thumbUrls);
        }
      } catch (error) {
        console.error("Error in fetchData:", error);
      }
    }

    fetchData();
  }, [citations, itemService]);

  // Function to combine citation data with content data
  const combineContentData = (citationsArray, contentData) => {
    if (!Array.isArray(citationsArray) || !contentData?.content) {
      return [];
    }

    return contentData.content
      .filter((contentItem) => contentItem?.ContentId) // Filter out items without ContentId
      .map((contentItem) => {
        // Find all matching citations for this content item
        const matchingCitations = citationsArray
          .filter(
            (citation) =>
              citation?.content_id &&
              citation.content_id.toLowerCase() ===
                contentItem.ContentId.toLowerCase()
          )
          .map((citation) => ({
            StartTime: citation.start_time,
            PageNumber: citation.page_number ? citation.page_number + 1 : 1, //Azure AI Search is 0 based page number
            Content: citation.content,
            ContentId: citation.content_id,
            CitationIndex: citation.index,
          }));

        // Only include items that have matching citations
        return matchingCitations.length > 0
          ? {
              ...contentItem,
              citations: matchingCitations,
            }
          : null;
      })
      .filter(Boolean); // Remove any null entries
  };

  useEffect(() => {
    async function fetchData() {
      try {
        if (!Array.isArray(citations) || citations.length === 0) {
          return;
        }

        const validCitations = citations.filter(
          (citation) => citation?.content_id
        );
        if (validCitations.length === 0) {
          return;
        }

        const result = await vlService
          .getContent({
            contentIds: validCitations.map((c) => c.content_id),
          })
          .catch((error) => {
            console.error("Error fetching content:", error);
            return null;
          });

        if (result?.Data) {
          const combined = combineContentData(validCitations, result.Data);
          setCombinedData(Array.isArray(combined) ? combined : []);
        }
      } catch (error) {
        console.error("Error in fetchData:", error);
        setCombinedData([]);
      }
    }
    fetchData();
  }, [citations, vlService]);

  const thumbnailStyle = {
    flex: "0 0 100px", // Fixed width for the thumbnail/icon column
    marginRight: "1rem",
    textAlign: "center",
  };

  const imageStyle = {
    width: "100%",
    height: "auto",
    display: "block",
  };

  const iconPlaceholderStyle = {
    // Style for the document icon placeholder
    fontSize: "3rem", // Adjust size as needed
    color: "#6c757d", // A neutral color
  };

  const infoStyle = {
    flex: "1", // Takes up remaining space
    marginRight: "1rem",
  };

  const titleStyle = {
    margin: "0 0 0.25rem 0",
    fontSize: "1.1rem",
    fontWeight: "bold",
  };

  const dateStyle = {
    margin: "0",
    fontSize: "0.85rem",
    color: "#6c757d",
  };

  const detailsStyle = {
    flex: "1", // Takes up remaining space
    paddingLeft: "1rem", // Add some padding for the list
    margin: 0,
    listStyle: "disc",
  };

  const detailItemStyle = {
    marginBottom: "0.25rem",
    fontSize: "0.9rem",
  };

  // Handle search text change
  const handleSearchChange = (e) => {
    const newSearchText = e.target.value;
    console.log(
      "[citationResults.js] handleSearchChange - newSearchText is:",
      `"${newSearchText}"`
    );
    setSearchText(newSearchText); // Always update searchText to reflect input

    // Search terms for PdfViewer will be updated via debouncedSearchTextForPdf
    // We still update the local searchTerms for other potential uses or direct feedback if needed
    if (newSearchText.trim()) {
      setSearchTerms([newSearchText]); // This updates the terms used by citation list itself, if any
    } else {
      setSearchTerms([]);
    }
  };

  // Handle search terms change from PdfViewer
  const handleSearchTermsChange = useCallback(
    (terms) => {
      if (terms && terms.length > 0) {
        // Join all terms with spaces to maintain the full sequence
        const newSearchText = terms.join(" ");
        // Only update if the search text is actually changing
        if (searchText !== newSearchText) {
          setSearchText(newSearchText);
        }
        // Only update search terms if they're different
        if (JSON.stringify(searchTerms) !== JSON.stringify(terms)) {
          setSearchTerms(terms);
        }
      } else if (searchText !== "" || searchTerms.length > 0) {
        // Only update if we need to clear the search
        setSearchText("");
        setSearchTerms([]);
      }
    },
    [searchText, searchTerms]
  );

  // Handle document load success
  const onDocumentLoadSuccess = ({ numPages: nextNumPages }) => {
    console.log(
      `citationResults: Document loaded successfully with ${nextNumPages} pages.`
    );
    setNumPages(nextNumPages);

    if (!initialLoadDoneRef.current) {
      const target = targetPageRef.current;
      console.log(
        `citationResults: Initial load. Target page from ref is ${target}.`
      );
      if (target && target > 0 && target <= nextNumPages) {
        console.log(`citationResults: Jumping to target page ${target}.`);
        setPageNumber(target);
      }
      initialLoadDoneRef.current = true; // Mark initial load as done
    }
  };

  const onDocumentLoadError = (error) => {
    console.error("App.js: PDF Document load error:", error.message);
    setNumPages(null); // Reset numPages as document failed to load
    // Optionally, you could also setPageNumber(1) or clear selectedPdf here
  };

  const handleDownloadPdf = () => {
    if (selectedPdf) {
      const link = document.createElement("a");
      link.href = selectedPdf; // selectedPdf should be the SAS URL
      link.setAttribute("download", "citation.pdf"); // Or a more dynamic name if available
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.warn("No PDF selected to download.");
    }
  };

  // Debounce searchText for PDF highlighting to avoid rapid updates
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTextForPdf(searchText);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  // Highlighting is now handled by PdfViewer's customTextLayerRenderer

  // Update viewer size when container resizes or left panel width changes
  useEffect(() => {
    if (!pdfContainerRef.current) return;

    const updateViewerSize = () => {
      if (pdfContainerRef.current) {
        const container = pdfContainerRef.current;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        // Calculate maximum possible width (account for padding and margins)
        const maxPossibleWidth = containerWidth - 40; // Account for padding
        const maxPossibleHeight = containerHeight - 40; // Account for padding

        // Calculate width based on container height (maintaining A4 aspect ratio 8.27/11.69 ≈ 0.707)
        // Use 100% of the available height minus padding
        const widthFromHeight = maxPossibleHeight * 0.707; // A4 ratio (height * 8.27/11.69)

        // Start with the smaller of the two widths to prevent horizontal scrolling
        let newWidth = Math.min(maxPossibleWidth, widthFromHeight);

        // If we have extra horizontal space, use more of it (up to 95% of container width)
        if (newWidth < maxPossibleWidth * 0.95) {
          newWidth = maxPossibleWidth * 0.95; //.min(maxPossibleWidth * 0.95, widthFromHeight * 1.2);
        }

        // Ensure we don't go below a minimum width
        newWidth = Math.max(newWidth, 400); // Minimum width of 400px for readability

        setViewerWidth(newWidth);
      }
    };

    // Initial size
    updateViewerSize();

    const resizeObserver = new ResizeObserver(updateViewerSize);

    if (pdfContainerRef.current) {
      resizeObserver.observe(pdfContainerRef.current);
    }

    // Also listen to window resize for better responsiveness
    window.addEventListener("resize", updateViewerSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateViewerSize);
    };
  }, [isPdf, leftPanelWidth]);

  // Handle resize mouse events
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;

      const container = containerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const newWidth = e.clientX - containerRect.left;

      // Enforce min and max width for the left panel
      const minWidth = 200;
      const maxWidth = 500;

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setLeftPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = "auto";
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  if (!combinedData || combinedData.length === 0) {
    return <div>Loading content...</div>; // Or some other placeholder/empty state
  }

  const loadPdfMetadata = (url, initialPageToShow) => {
    console.log(
      `citationResults: loadPdfMetadata called with initialPageToShow: ${initialPageToShow}`
    );
    setSelectedPdf(url);
    setPageNumber(initialPageToShow); // Set to initial page (e.g., 1)
    setIsPdf(true);
    setNumPages(null); // Reset numPages to ensure clean state for new PDF/page
  };

  const closePdfViewer = () => {
    setIsPdf(false);
    setSelectedPdf(null);
    setNumPages(null);
  };

  const previousPage = () => {
    setPageNumber(pageNumber === 1 ? numPages : pageNumber - 1);
  };

  const nextPage = () => {
    setPageNumber(pageNumber === numPages ? 1 : pageNumber + 1);
  };

  // handleDownloadClick removed as it's a duplicate of handleDownloadPdf

  const handleCitationItemClicked = async (
    contentId,
    pageNumber,
    startSeconds,
    content = ""
  ) => {
    setSelectedCitation({
      ContentId: contentId,
      StartSeconds: startSeconds,
      PageNumber: pageNumber,
    });
    setSelectedCitationContent(content || "");

    // Update search terms with clean sequences from the new citation content
    if (content) {
      const sequences = extractCleanSequences(content, 5, 10); // Get first 5-word sequence
      if (sequences.length > 0) {
        setSearchText(sequences[0]);
        setSearchTerms([sequences[0]]);
      } else {
        setSearchText("");
        setSearchTerms([]);
      }
    }

    // Find the content item from combinedData
    const contentItem = combinedData.find(
      (item) =>
        item.ContentId &&
        typeof item.ContentId === "string" &&
        contentId &&
        typeof contentId === "string" &&
        item.ContentId.toLowerCase() === contentId.toLowerCase()
    );

    let isPdf = false;
    if (contentItem) {
      if (
        contentItem.FileExt &&
        typeof contentItem.FileExt === "string" &&
        contentItem.FileExt.toLowerCase() === "pdf"
      ) {
        isPdf = true;
      }
    } else {
      console.warn(
        `Content item with ID ${contentId} not found in combinedData.`
      );
    }

    if (isPdf && pageNumber) {
      console.log(
        `Clicked on PDF content: ${
          contentItem.Title || contentItem.FileName
        }, page: ${pageNumber}. Fetching SAS URL...`
      );
      try {
        const response = await vlService.getSasTokenedContent(contentId);
        if (response && response.Success && response.Data) {
          const sasUrl = response.Data.url; // Assuming response.Data is the SAS URL string
          console.log("SAS URL received:", sasUrl);
          let citationPage = 1; // Default to page 1
          if (pageNumber) {
            // pageNumber here is from the citation argument
            const parsedPage = parseInt(pageNumber, 10);
            if (!isNaN(parsedPage) && parsedPage > 0) {
              citationPage = parsedPage;
            }
          }
          targetPageRef.current = citationPage; // Store the intended page from citation
          initialLoadDoneRef.current = false; // Reset for new PDF/page selection
          console.log(
            `citationResults: Set targetPageRef to ${citationPage} and initialLoadDoneRef to false.`
          );
          // Open in a new tab. The #page=N part is a hint for PDF viewers.
          //window.open(`${sasUrl}#page=${targetPageRef.current}`, '_blank');
          loadPdfMetadata(sasUrl, 1); // Always load page 1 initially, onDocumentLoadSuccess will handle jump
        } else {
          console.error(
            "Failed to get SAS tokened URL:",
            response ? response.DisplayMessage : "No response"
          );
          // Fallback or show error: navigate to a generic content page or show an error to the user
          navigate(`/content?k=${contentId}&pg=${pageNumber}`);
        }
      } catch (error) {
        console.error("Error calling getSasTokenedContent:", error);
        navigate(`/content?k=${contentId}&pg=${pageNumber}`);
      }
    } else if (startSeconds) {
      console.log(
        `Clicked on content without page number (e.g., video): ${
          contentItem?.Title || contentItem?.FileName
        }, startSeconds: ${startSeconds}`
      );
      navigate(`/content?k=${contentId}&st=${startSeconds}`);
    } else {
      console.log(
        `Clicked on non-PDF content with page number: ${
          contentItem?.Title || contentItem?.FileName
        }, page: ${pageNumber}`
      );
      navigate(`/content?k=${contentId}&pg=${pageNumber}`);
    }
  };

  return (
    <div className="virtual-librarian-citation-results">
      {/* ContentModal: Only show if PDF viewer is NOT active */}
      {!isPdf && (
        <ContentModal
          open={playerOpen}
          setOpen={() => setPlayerOpen(false)}
          handleOk={() => setPlayerOpen(false)}
          contentId={selectedCitation?.ContentId}
          timestamp={selectedCitation?.StartSeconds}
        />
      )}

      {/* PDF Viewer Area: Only show when PDF is selected */}
      {isPdf && (
        <div
          className="pdf-viewer"
          style={{
            position: "relative",
            border: "1px solid #eee",
            display: "flex",
            flexDirection: "row",
            height: "80vh",
            width: "100%",
            overflow: "hidden",
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
          ref={containerRef}
        >
          {/* Global styles for hiding scrollbars */}
          <style>
            {`
              .pdf-viewer-wrapper {
                flex: 1;
                overflow: auto;
                -ms-overflow-style: none;
                scrollbar-width: none;
              }
              .pdf-viewer-wrapper::-webkit-scrollbar {
                display: none;
              }
              .pdf-viewer-content {
                width: 100%;
                height: 100%;
                overflow: hidden;
                display: flex;
                flex-direction: column;
              }
              .pdf-viewer-inner {
                flex: 1;
                overflow: hidden;
                display: flex;
                justify-content: center;
                padding: 20px 0;
              }
            `}
          </style>
          {/* Left sidebar - Resizable */}
          <div
            style={{
              width: `${leftPanelWidth}px`,
              minWidth: "200px",
              maxWidth: "500px",
              display: "flex",
              flexDirection: "column",
              flexShrink: 0,
              overflowY: "auto",
              gap: "20px",
              padding: "20px",
              borderRight: "1px solid #eee",
              backgroundColor: "#f8f9fa",
              height: "100%",
              boxSizing: "border-box",
              position: "relative",
            }}
          >
            {/* Search Box */}
            <div
              className="search-container"
              style={{ width: "100%", position: "relative" }}
            >
              <div style={{ position: "relative", width: "100%" }}>
                <input
                  type="text"
                  placeholder="Search text in PDF..."
                  value={searchText}
                  onChange={handleSearchChange}
                  className="search-input"
                  style={{
                    borderRadius: "4px",
                    paddingRight: "30px",
                    fontSize: "0.9rem",
                    backgroundColor: "white",
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
              {searchText && (
                <button
                  onClick={() => setSearchText("")}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#999",
                    padding: "5px",
                  }}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Navigation and Download Buttons */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                padding: "10px 0",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={previousPage}
                  fullWidth
                  style={{ marginRight: "5px", backgroundColor: "#003c5b" }}
                >
                  Previous
                </Button>
                <Typography
                  variant="body2"
                  style={{ minWidth: "45px", textAlign: "center" }}
                >
                  {pageNumber || 0} / {numPages || 0}
                </Typography>
                <Button
                  variant="contained"
                  onClick={nextPage}
                  fullWidth
                  style={{ marginLeft: "5px", backgroundColor: "#003c5b" }}
                >
                  Next
                </Button>
              </div>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPdf}
                disabled={!selectedPdf}
                fullWidth
              >
                Download PDF
              </Button>
            </div>

            {/* Citation Context Box */}
            {selectedCitationContent && (
              <div
                style={{
                  flex: 1,
                  maxHeight: "300px",
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  padding: "10px",
                  backgroundColor: "#f8f9fa",
                }}
              >
                <div
                  style={{
                    fontWeight: "bold",
                    marginBottom: "8px",
                    fontSize: "0.9em",
                    color: "#333",
                  }}
                >
                  Context Suggestion:
                </div>
                <div
                  style={{
                    fontStyle: "italic",
                    fontSize: "0.9em",
                    lineHeight: "1.4",
                  }}
                >
                  {extractCleanSequences(selectedCitationContent, 5, 10).map(
                    (sequence, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          setSearchText(sequence);
                          setSearchTerms([sequence]);
                        }}
                        style={{
                          padding: "6px 8px",
                          margin: "4px 0",
                          borderRadius: "4px",
                          cursor: "pointer",
                          backgroundColor:
                            searchText === sequence ? "#e3f2fd" : "transparent",
                          border:
                            searchText === sequence
                              ? "1px solid #90caf9"
                              : "1px solid transparent",
                          "&:hover": {
                            backgroundColor: "#f0f0f0",
                          },
                        }}
                      >
                        "{sequence}"
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Resize handle */}
          <div
            style={{
              width: "10px",
              cursor: "col-resize",
              backgroundColor: isResizing ? "#1976d2" : "transparent",
              transition: "background-color 0.2s",
              "&:hover": {
                backgroundColor: "#1976d2",
              },
              alignSelf: "stretch",
              margin: "0 -5px",
              zIndex: 10,
            }}
            onMouseDown={() => setIsResizing(true)}
            onMouseEnter={() => (document.body.style.cursor = "col-resize")}
            onMouseLeave={() =>
              !isResizing && (document.body.style.cursor = "auto")
            }
          />

          {/* PDF Viewer Container */}
          <div className="pdf-viewer-wrapper" ref={pdfContainerRef}>
            <div
              className="pdf-viewer-content"
              style={{
                backgroundColor: "#f5f5f5",
                padding: "20px",
                position: "relative",
                borderRadius: "4px",
                border: "1px solid #e0e0e0",
                height: "100%",
                boxSizing: "border-box",
              }}
            >
              <Button
                variant="outlined"
                onClick={closePdfViewer}
                startIcon={<CloseIcon />}
                style={{
                  position: "absolute",
                  top: "15px",
                  right: "45px",
                  zIndex: 10,
                  fontSize: "0.9rem",
                  color: "white",
                  backgroundColor: "#003c5b",
                }}
              >
                Close Viewer
              </Button>
              {selectedPdf && (
                <div className="pdf-viewer-inner">
                  <PdfViewer
                    selectedPdf={selectedPdf}
                    pageNumber={pageNumber}
                    numPages={numPages}
                    onDocumentLoadSuccess={onDocumentLoadSuccess}
                    onDocumentLoadError={onDocumentLoadError}
                    textLayerRef={textLayerRef}
                    width={viewerWidth}
                    citationContent={selectedCitationContent}
                    searchTerms={
                      debouncedSearchTextForPdf
                        ? [debouncedSearchTextForPdf]
                        : []
                    }
                    onSearchTermsChange={handleSearchTermsChange}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Citations List: Only show if PDF viewer is NOT active */}
      {!isPdf && (
        <>
          {combinedData.map((item) => (
            <div key={item.ContentId} className="vl-list">
              {/* 1. Thumbnail or Icon */}
              <div style={thumbnailStyle}>
                {thumbnailUrls[item.ContentId] &&
                !imageLoadFailed?.[item.ContentId] ? (
                  <img
                    src={thumbnailUrls[item.ContentId]}
                    alt="Thumbnail"
                    style={imageStyle}
                    onClick={() =>
                      handleCitationItemClicked(
                        item.ContentId,
                        0,
                        0,
                        item.Content || ""
                      )
                    }
                    onError={() => {
                      setImageLoadFailed((prev) => ({
                        ...prev,
                        [item.ContentId]: true,
                      }));
                    }}
                  />
                ) : (
                  <div style={iconPlaceholderStyle}>
                    {item.ContentType.toLowerCase() === "video" ? "🎥" : "📄"}
                  </div>
                )}
              </div>

              {/* 2. Title and Date */}
              <div style={infoStyle}>
                <h4 style={titleStyle}>{item.Title || "Untitled Content"}</h4>
                <p style={dateStyle}>
                  Created:{" "}
                  {item.CreatedOn
                    ? new Date(item.CreatedOn).toLocaleDateString()
                    : "Unknown Date"}
                </p>
                <p>Citations: {item.citations?.length || 0}</p>
                <p style={dateStyle}>File Type: {item.FileExt || "Unknown"}</p>
              </div>

              {/* 3. Details (Max 10) */}
              <ul style={detailsStyle}>
                {item.citations &&
                  item.citations.length > 0 &&
                  [...item.citations]
                    .sort((a, b) => {
                      if (a.CitationIndex == null && b.CitationIndex == null)
                        return 0;
                      if (a.CitationIndex == null) return 1;
                      if (b.CitationIndex == null) return -1;
                      return a.CitationIndex - b.CitationIndex;
                    })
                    .slice(0, 10)
                    .map((citation, index) => (
                      <li
                        key={index}
                        style={{
                          ...detailItemStyle,
                          listStyleType: "none",
                          display: "flex",
                          alignItems: "baseline",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          handleCitationItemClicked(
                            citation.ContentId,
                            citation.PageNumber,
                            getSeconds(citation.StartTime),
                            citation.Content || ""
                          )
                        }
                      >
                        <span style={{ marginRight: "8px", flexShrink: 0 }}>
                          <img
                            src={videoIcon}
                            alt="Video"
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCitationItemClicked(
                                citation.ContentId,
                                citation.PageNumber,
                                getSeconds(citation.StartTime),
                                citation.Content || ""
                              );
                            }}
                          />
                        </span>
                        <div>
                          {citation.StartTime && (
                            <>
                              <b>{citation.CitationIndex}: </b>(
                              {citation.StartTime}){" "}
                              <CitationTooltip content={citation.Content} />
                            </>
                          )}
                          {citation.PageNumber && (
                            <>
                              <b>{citation.CitationIndex}: </b>(Page{" "}
                              {citation.PageNumber}){" "}
                              <CitationTooltip content={citation.Content} />
                            </>
                          )}
                        </div>
                      </li>
                    ))}
                {(!item.citations || item.citations.length === 0) && (
                  <li
                    style={{
                      ...detailItemStyle,
                      listStyle: "none",
                      color: "#6c757d",
                    }}
                  >
                    (No citations available)
                  </li>
                )}
              </ul>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default VirtualLibrarianCitationResults;
